/**
 * Mise à jour guidée des anciens APK (versionCode < 70, avant 1.5.9.33).
 *
 * Ces APK téléchargent bien la nouvelle version, mais leur récepteur de fin de
 * téléchargement ne reçoit rien sous Android 13 : l'écran « Installer » d'Android
 * n'apparaît qu'au lancement suivant de l'appli. La page propose donc UNE fois par
 * jour et par version, caisse au repos, de terminer la mise à jour en fermant l'appli
 * au bon moment. Les APK ≥ 70 s'installent seuls : rien ne s'affiche pour eux.
 */

/** 1.5.9.33 (versionCode 70) : premier APK qui installe seul, caisse inactive. */
export const GUIDED_UPDATE_MAX_CODE = 70;
export const GUIDED_OFFER_COOLDOWN_MS = 24 * 60 * 60 * 1000;
/** Après une fermeture voulue par le parcours, la suite est proposée une seule fois. */
export const GUIDED_RESUME_WINDOW_MS = 30 * 60 * 1000;
/** Délai sans aucun signal de l'APK après « Mettre à jour ». */
export const GUIDED_NO_EVENT_MS = 20 * 1000;
/** Délai sans progression du téléchargement avant le repli manuel. */
export const GUIDED_STALL_MS = 60 * 1000;
export const GUIDED_STORAGE_KEY = 'vp_guided_update';

// Statuts DownloadManager remontés par l'APK dans les événements 'download-progress'.
const DM_STATUS_SUCCESSFUL = 8;
const DM_STATUS_FAILED = 16;

export type GuidedManifest = { versionCode: number; forceUpdate: boolean; silent?: boolean };

export type GuidedRecord = {
  code: number;
  shownAt: number;
  restartedAt?: number;
  resumeUsed?: boolean;
};

export type GuidedOfferInput = {
  /** versionCode lu sur le pont natif ; null hors APK (navigateur, PWA). */
  nativeCode: number | null;
  manifest: GuidedManifest | null;
  locked: boolean;
  idle: boolean;
  /** Une proposition de mise à jour (toast ou guidée) a déjà été faite dans la session. */
  shownThisSession: boolean;
  record: GuidedRecord | null;
  now: number;
};

export type GuidedOffer = { show: false; reason: string } | { show: true; resume: boolean };

export function isGuidedCandidate(nativeCode: number | null, manifest: GuidedManifest | null) {
  if (nativeCode === null || nativeCode >= GUIDED_UPDATE_MAX_CODE) return false;
  return manifest !== null && manifest.forceUpdate && manifest.versionCode > nativeCode;
}

export function decideGuidedOffer(input: GuidedOfferInput): GuidedOffer {
  const { nativeCode, manifest, record, now } = input;
  if (nativeCode === null) return { show: false, reason: 'pas une caisse Android' };
  if (nativeCode >= GUIDED_UPDATE_MAX_CODE) return { show: false, reason: 'APK autonome' };
  if (!manifest || !isGuidedCandidate(nativeCode, manifest)) {
    return { show: false, reason: 'aucune mise à jour forcée' };
  }
  if (input.locked) return { show: false, reason: 'mises à jour verrouillées' };
  if (input.shownThisSession) return { show: false, reason: 'déjà proposée dans la session' };
  if (!input.idle) return { show: false, reason: 'caisse occupée' };

  const sameVersion = record !== null && record.code === manifest.versionCode;
  if (!sameVersion) return { show: true, resume: false };
  if (
    record.restartedAt !== undefined &&
    !record.resumeUsed &&
    now - record.restartedAt < GUIDED_RESUME_WINDOW_MS
  ) {
    return { show: true, resume: true };
  }
  if (now - record.shownAt < GUIDED_OFFER_COOLDOWN_MS) {
    return { show: false, reason: 'déjà proposée aujourd’hui' };
  }
  return { show: true, resume: false };
}

/** Le relevé de suite n'est valable qu'une fois, juste après la fermeture voulue. */
export function recordAfterOffer(
  previous: GuidedRecord | null,
  code: number,
  resume: boolean,
  now: number,
): GuidedRecord {
  if (resume && previous) return { ...previous, resumeUsed: true };
  return { code, shownAt: now };
}

export function recentlyRestarted(record: GuidedRecord | null, code: number, now: number) {
  return (
    record !== null &&
    record.code === code &&
    record.restartedAt !== undefined &&
    now - record.restartedAt < GUIDED_RESUME_WINDOW_MS
  );
}

export function parseGuidedRecord(raw: string | null): GuidedRecord | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const { code, shownAt, restartedAt, resumeUsed } = value as Record<string, unknown>;
    if (typeof code !== 'number' || typeof shownAt !== 'number') return null;
    return {
      code,
      shownAt,
      ...(typeof restartedAt === 'number' ? { restartedAt } : {}),
      ...(resumeUsed === true ? { resumeUsed: true } : {}),
    };
  } catch {
    return null;
  }
}

// ─── Suivi du téléchargement (événements 'vp-updater' de l'APK) ─────────────

export type UpdaterEvent = { type?: string; percent?: number; status?: number };

export type DownloadTrack = {
  ready: boolean;
  installing: boolean;
  failed: boolean;
  percent: number | null;
  lastEventAt: number | null;
  lastProgressAt: number | null;
};

export const EMPTY_TRACK: DownloadTrack = {
  ready: false,
  installing: false,
  failed: false,
  percent: null,
  lastEventAt: null,
  lastProgressAt: null,
};

const INSTALL_EVENTS = new Set(['install-start', 'install-intent', 'install-fallback-intent']);
const FAILURE_EVENTS = new Set(['download-failed', 'install-failed']);

export function trackUpdaterEvent(
  track: DownloadTrack,
  event: UpdaterEvent,
  now: number,
): DownloadTrack {
  const next: DownloadTrack = { ...track, lastEventAt: now };
  const type = event.type ?? '';
  if (INSTALL_EVENTS.has(type)) return { ...next, installing: true };
  if (FAILURE_EVENTS.has(type)) return { ...next, failed: true };
  if (type === 'download-complete') return { ...next, ready: true, percent: 100 };
  if (type === 'download-start') return { ...next, failed: false, lastProgressAt: now };
  if (type !== 'download-progress') return next;
  if (event.status === DM_STATUS_SUCCESSFUL) return { ...next, ready: true, percent: 100 };
  if (event.status === DM_STATUS_FAILED) return { ...next, failed: true };
  const percent = typeof event.percent === 'number' && event.percent >= 0 ? event.percent : null;
  const progressed = percent !== null && (track.percent === null || percent > track.percent);
  return progressed ? { ...next, percent, lastProgressAt: now } : next;
}

export type GuidedStep = 'downloading' | 'ready' | 'restart' | 'installing' | 'blocked';

/**
 * Étape suivante après « Mettre à jour ».
 * - prêt (fichier téléchargé) → fermer l'appli : sa réouverture affiche « Installer » ;
 * - aucun signal de l'APK → téléchargement lancé plus tôt dans la session, sa fin n'est
 *   jamais remontée : une fermeture suffit, une seule fois (ensuite repli manuel) ;
 * - aucune progression pendant 60 s → téléchargement bloqué : repli manuel.
 */
export function nextGuidedStep(
  track: DownloadTrack,
  clickedAt: number,
  now: number,
  restartedBefore: boolean,
): GuidedStep {
  if (track.installing) return 'installing';
  if (track.ready) return 'ready';
  if (track.failed) return 'blocked';
  const heardSinceClick = track.lastEventAt !== null && track.lastEventAt >= clickedAt;
  if (!heardSinceClick && now - clickedAt >= GUIDED_NO_EVENT_MS) {
    return restartedBefore ? 'blocked' : 'restart';
  }
  const progressRef = Math.max(track.lastProgressAt ?? clickedAt, clickedAt);
  if (now - progressRef >= GUIDED_STALL_MS) return 'blocked';
  return 'downloading';
}
